import type { InputHTMLAttributes, ChangeEvent, Ref } from 'react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';

const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/** Keeps the digits only, folding Arabic-Indic numerals to Latin so a phone keyboard works too. */
function digitsOf(input: string) {
  let out = '';
  for (const ch of input) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
      continue;
    }
    const i = ARABIC_DIGITS.indexOf(ch) >= 0 ? ARABIC_DIGITS.indexOf(ch) : PERSIAN_DIGITS.indexOf(ch);
    if (i >= 0) out += i;
  }
  return out;
}

/** Index just after the nth digit of `text` (0 → start of the string). */
function caretAfterDigit(text: string, n: number) {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i]! >= '0' && text[i]! <= '9' && ++seen === n) return i + 1;
  }
  return text.length;
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'> & {
  /** Integer dinars. `''`/null/undefined render as an empty field. */
  value: number | '' | null | undefined;
  onChange: (value: number) => void;
  ref?: Ref<HTMLInputElement>;
};

/**
 * Money/price entry that groups thousands while typing ("1,500,000") and reports
 * a plain integer. Separators are inserted on every keystroke, so the caret is
 * repositioned by digit count rather than by character offset.
 */
export function MoneyInput({ value, onChange, className, ref, ...rest }: Props) {
  const text = value === '' || value === null || value === undefined ? '' : formatNumber(Number(value));

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const el = e.currentTarget;
    const typed = el.value;
    const digitsBeforeCaret = digitsOf(typed.slice(0, el.selectionStart ?? typed.length)).length;
    const digits = digitsOf(typed).replace(/^0+(?=\d)/, '');
    const next = digits === '' ? 0 : Number(digits);
    const nextText = digits === '' ? '' : formatNumber(next);
    // Write the grouped text back before React does, so the caret we set survives the re-render.
    el.value = nextText;
    const caret = caretAfterDigit(nextText, digitsBeforeCaret);
    el.setSelectionRange(caret, caret);
    onChange(next);
  }

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={cn('input', className)}
      value={text}
      onChange={handleChange}
      {...rest}
    />
  );
}
